'use client';

import { useEffect } from 'react';
import { create } from 'zustand';
import { getApiBaseUrl } from '@/utils/apiBaseUrl';
import { recordDeviceAccount } from '@/utils/deviceAccounts';

interface AuthState {
  isAuthenticated: boolean;
  /** True after the first client-side rehydrate (GET /api/auth/me) has
   * run. Used by callers that need to wait for a real anon/authed
   * answer instead of the pre-hydration `isAuthenticated: false`
   * placeholder. */
  hydrated: boolean;
  userId: string | null;
  /** Telegram signs initData once per mini-app launch and never refreshes it,
   * so an expired one cannot be renewed in place — every request keeps failing
   * until the user reopens the app. */
  sessionExpired: boolean;
  markAuthed: (userId: string) => void;
  markAnon: () => void;
  markSessionExpired: () => void;
  _rehydrate: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  hydrated: false,
  userId: null,
  sessionExpired: false,

  markSessionExpired: () => {
    set({ sessionExpired: true });
  },

  markAuthed: (userId: string) => {
    recordDeviceAccount(userId);
    set({ isAuthenticated: true, userId, hydrated: true });
  },

  markAnon: () => {
    set({ isAuthenticated: false, userId: null, hydrated: true });
  },

  _rehydrate: async () => {
    if (typeof window === 'undefined') return;
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'X-Requested-With': 'shot' },
      });
      if (res.ok) {
        const data = (await res.json()) as { userId?: string | null };
        if (data?.userId) {
          recordDeviceAccount(data.userId);
          set({ isAuthenticated: true, userId: data.userId, hydrated: true });
          return;
        }
      }
      set({ isAuthenticated: false, userId: null, hydrated: true });
    } catch {
      set({ isAuthenticated: false, userId: null, hydrated: true });
    }
  },
}));

/**
 * Mount once at the root of the client tree (e.g. providers.tsx) to
 * resolve the visitor's auth state from the backend. The cookie is
 * httpOnly so the client cannot inspect it directly — `/api/auth/me`
 * is the only way to know whether we're authed at app boot.
 */
export function useAuthStoreHydrate(): void {
  useEffect(() => {
    void useAuthStore.getState()._rehydrate();
  }, []);
}
